import {
	dollarToNumber,
	excelSerialToJsDate,
	numberToCents,
	stringToNumber,
} from '@marmon/shared/types/common';
import { Timestamp } from 'firebase/firestore';
import { ParseInvoiceError, RawInvoiceKey } from '../interfaces';

export const isEmpty = (value: any) => {
	if (typeof value === 'undefined' || value === null) return true;
	else if (typeof value === 'string') return !value.trim();
	else if (typeof value === 'number') return Number.isNaN(value);
	else if (Array.isArray(value)) return !value.length;
	else if (typeof value === 'object') return !Object.keys(value).length;
	return false;
};

type FormatterCallback<T = any> = {
	error?: (value: ParseInvoiceError) => void;
	complete: (value: T) => void;
};

type FormatterOptions = { row: number };

function CustomerFormatter(
	value: string,
	callback: FormatterCallback<string>,
	options: FormatterOptions,
) {
	const { error, complete } = callback;
	const { row } = options;
	const sendError = (value: ParseInvoiceError) => (error ? error(value) : {});

	value = value.trim();
	if (isEmpty(value)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: The "Customer" value was not provided',
			messageHtml:
				'Invalid value: The "<b>Customer</b>" value was not provided',
		});
		return;
	}

	complete(value);
}

function ProductFormatter(
	value: string,
	callback: FormatterCallback<string>,
	options: FormatterOptions,
) {
	const { error, complete } = callback;
	const { row } = options;
	const sendError = (value: ParseInvoiceError) => (error ? error(value) : {});

	value = value.trim();
	if (isEmpty(value)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: The "Product" value was not provided',
			messageHtml:
				'Invalid value: The "<b>Product</b>" value was not provided',
		});
		return;
	}

	complete(value);
}

function SegmentFormatter(value: string, callback: FormatterCallback<string>) {
	const { complete } = callback;
	value = value.trim();
	complete(value);
}

function InvoiceDateFormatter(
	value: string,
	callback: FormatterCallback<Timestamp>,
	options: FormatterOptions,
) {
	const { error, complete } = callback;
	const { row } = options;
	const sendError = (value: ParseInvoiceError) => (error ? error(value) : {});

	if (isEmpty(value)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: The "Invoice Date" value was not provided',
			messageHtml:
				'Invalid value: The "<b>Invoice Date</b>" value was not provided',
		});
		return;
	}

	let convert = new Date();
	try {
		// likely a formatted date mm/dd/yyy or mm-dd-yyyy
		if (value.includes('/') || value.includes('-')) {
			convert = new Date(value);
		} else {
			const serial = stringToNumber(value);
			if (Number.isNaN(serial)) {
				sendError({
					row,
					code: 'InvalidValue',
					type: 'InsufficientData',
					message:
						'Invalid value: A formatted Date or Excel Serial Number is required to parse "Invoice Date".',
					messageHtml:
						'Invalid value: A formatted Date or Excel Serial Number is required to parse "<b>Invoice Date</b>".',
				});
				return;
			}
			convert = excelSerialToJsDate(serial);
		}
	} catch {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message:
				'Invalid value: A formatted Date or Excel Serial Number is required to parse "Invoice Date".',
			messageHtml:
				'Invalid value: A formatted Date or Excel Serial Number is required to parse "<b>Invoice Date</b>".',
		});
		return;
	}

	if (Number.isNaN(convert.valueOf())) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message:
				'Invalid value: A formatted Date or Excel Serial Number is required to parse "Invoice Date".',
			messageHtml:
				'Invalid value: A formatted Date or Excel Serial Number is required to parse "<b>Invoice Date</b>".',
		});
		return;
	}

	let timestamp: Timestamp | null = null;
	try {
		timestamp = Timestamp.fromMillis(convert.getTime());
	} catch {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message:
				'Invalid value: A formatted Date or Excel Serial Number is required to parse "Invoice Date".',
			messageHtml:
				'Invalid value: A formatted Date or Excel Serial Number is required to parse "<b>Invoice Date</b>".',
		});
		return;
	}

	complete(timestamp);
}

function CostFormatter(
	value: string,
	callback: FormatterCallback<number>,
	options: FormatterOptions,
) {
	const { error, complete } = callback;
	const { row } = options;
	const sendError = (value: ParseInvoiceError) => (error ? error(value) : {});

	if (isEmpty(value)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: The "Cost" value was not provided',
			messageHtml:
				'Invalid value: The "<b>Cost</b>" value was not provided',
		});
		return;
	}

	const dollars = dollarToNumber(value);
	if (Number.isNaN(dollars)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: A number is required to parse "Cost".',
			messageHtml:
				'Invalid value: A number is required to parse "<b>Cost</b>".',
		});
		return;
	}

	const cents = numberToCents(dollars);
	if (Number.isNaN(cents)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: A number is required to parse "Cost".',
			messageHtml:
				'Invalid value: A number is required to parse "<b>Cost</b>".',
		});
		return;
	}

	complete(cents);
}

function RevenueFormatter(
	value: string,
	callback: FormatterCallback<number>,
	options: FormatterOptions,
) {
	const { error, complete } = callback;
	const { row } = options;
	const sendError = (value: ParseInvoiceError) => (error ? error(value) : {});

	if (isEmpty(value)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: The "Revenue" value was not provided',
			messageHtml:
				'Invalid value: The "<b>Revenue</b>" value was not provided',
		});
		return;
	}

	const dollars = dollarToNumber(value);
	if (Number.isNaN(dollars)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: A number is required to parse "Revenue".',
			messageHtml:
				'Invalid value: A number is required to parse "<b>Revenue</b>".',
		});
		return;
	}

	const cents = numberToCents(dollars);
	if (Number.isNaN(cents)) {
		sendError({
			row,
			code: 'InvalidValue',
			type: 'InsufficientData',
			message: 'Invalid value: A number is required to parse "Revenue".',
			messageHtml:
				'Invalid value: A number is required to parse "<b>Revenue</b>".',
		});
		return;
	}

	complete(cents);
}

export const RawInvoiceFormatterMap = (): {
	[key in RawInvoiceKey]: (
		value: string,
		callback: FormatterCallback,
		options: FormatterOptions,
	) => void;
} => ({
	Customer: (
		value: string,
		callback: FormatterCallback<string>,
		options: FormatterOptions,
	) => CustomerFormatter(value, callback, options),

	'Part #': (
		value: string,
		callback: FormatterCallback<string>,
		options: FormatterOptions,
	) => ProductFormatter(value, callback, options),

	Segment: (value: string, callback: FormatterCallback<string>) =>
		SegmentFormatter(value, callback),

	'Invoice Date': (
		value: string,
		callback: FormatterCallback<Timestamp>,
		options: FormatterOptions,
	) => InvoiceDateFormatter(value, callback, options),

	Cost: (
		value: string,
		callback: FormatterCallback<number>,
		options: FormatterOptions,
	) => CostFormatter(value, callback, options),

	Revenue: (
		value: string,
		callback: FormatterCallback<number>,
		options: FormatterOptions,
	) => RevenueFormatter(value, callback, options),
});
